import { useState, useMemo, type ChangeEvent } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Empty,
  Alert,
  Collapse,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, AimOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  LesionType,
  Laterality,
  LesionStatus,
  SkinType,
  type CreateLesionDto,
  type LesionFull,
} from '../../../../api/dermatoApi';
import LesionPhotos from './LesionPhotos';
import { useNavigate } from 'react-router-dom';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import {
  AK_PHENOTYPE_OPTIONS,
  AK_SEVERITY_OPTIONS,
  AK_SPECIAL_AREA_OPTIONS,
  AkPhenotypeType,
} from '../../ak/akTypes';
import { BODY_REGIONS } from '../../ak/bodyMap';
import { FACE_REGIONS } from '../../ak/faceMap';
import { validateAkPhenotype, type AkPhenotypeInput } from '../../ak/akValidation';

const { Text } = Typography;

const LESION_TYPE_OPTIONS = Object.values(LesionType).map((v) => ({ value: v, label: v }));
const LATERALITY_OPTIONS = Object.values(Laterality).map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = Object.values(LesionStatus).map((v) => ({ value: v, label: v }));
const FITZPATRICK_OPTIONS = Object.values(SkinType).map((v) => ({ value: v, label: v.replace('TYPE_', 'Tip ') }));

const BODY_ZONE_OPTIONS = BODY_REGIONS.map((r) => ({ value: r.code, label: r.labelRo }));
const FACE_ZONE_OPTIONS = FACE_REGIONS.map((r) => ({ value: r.code, label: r.labelRo }));

const statusColor: Record<LesionStatus, string> = {
  NEW: 'blue',
  MONITORING: 'cyan',
  SUSPECT: 'orange',
  BENIGN: 'green',
  MALIGNANT: 'red',
  CLEAR: 'default',
};

const Step4Lesions = () => {
  const { patient, form, lesions, addLesion, updateLesion, removeLesion, selectLesion, loading } = useDermatoWizard();

  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LesionFull | null>(null);
  const [formAntd] = Form.useForm<CreateLesionDto>();
  const [saving, setSaving] = useState(false);
  /** Leziune de tip keratoză actinică (AK) — activează secțiunea de fenomenotip. */
  const [akEnabled, setAkEnabled] = useState(false);
  /** Datele fenomenotipului AK (local — backendul nu are încă endpoint dedicat). */
  const [ak, setAk] = useState<AkPhenotypeInput>({});
  const [akErrors, setAkErrors] = useState<string[]>([]);

  const openCreate = () => {
    setEditing(null);
    formAntd.resetFields();
    formAntd.setFieldsValue({
      type: LesionType.MACULE,
      laterality: Laterality.MIDLINE,
      status: LesionStatus.NEW,
      fitzpatrickType: SkinType.TYPE_III,
      isNewlyDiagnosed: true,
      recentChange: false,
      onsetDate: undefined,
    } as Partial<CreateLesionDto>);
    setAkEnabled(false);
    setAk({});
    setAkErrors([]);
    setModalOpen(true);
  };

  const openEdit = (l: LesionFull) => {
    setEditing(l);
    formAntd.setFieldsValue({
      ...l,
      onsetDate: l.onsetDate ? dayjs(l.onsetDate) : undefined,
    } as any);
    setAkEnabled(false);
    setAk({});
    setAkErrors([]);
    setModalOpen(true);
  };

  const updateAk = (patch: AkPhenotypeInput) => setAk((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (): Promise<boolean> => {
    if (!form) return false;
    // AK: pentru leziunile de tip keratoză actinică datele sunt obligatorii.
    const akResult = validateAkPhenotype(akEnabled ? ak : { phenotype: null });
    setAkErrors(akResult.errors);
    if (!akResult.valid) {
      message.error('Datele pentru keratoza actinică (AK) sunt incomplete.');
      return false;
    }
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateLesionDto = {
        ...values,
        formId: form.id,
        onsetDate: values.onsetDate ? (values.onsetDate as unknown as dayjs.Dayjs).toISOString() : undefined,
      };
      if (editing?.id) {
        const { formId: _formId, clinicalCode: _clinicalCode, ...patch } = dto;
        const updated = await updateLesion(editing.id, patch);
        if (!updated) return false;
      } else {
        const created = await addLesion(dto);
        if (!created) return false;
      }
      setModalOpen(false);
      return true;
    } catch {
      // validation errors surfaced by Form
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Salvarea pasului are loc în modal; în afara modalului nu există modificări nesalvate.
  const saveCurrentStep = async (): Promise<boolean> => {
    if (!modalOpen) return true;
    return handleSubmit();
  };

  useRegisterStepSave(saveCurrentStep);

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'clinicalCode', key: 'clinicalCode', width: 90 },
      { title: 'Localizare', dataIndex: 'location', key: 'location' },
      {
        title: 'Lateralitate',
        dataIndex: 'laterality',
        key: 'laterality',
        width: 110,
        render: (v: Laterality | null) => v ?? '—',
      },
      {
        title: 'Tip',
        dataIndex: 'type',
        key: 'type',
        width: 100,
        render: (v: LesionType) => <Tag>{v}</Tag>,
      },
      {
        title: 'Dimensiune',
        dataIndex: 'size',
        key: 'size',
        width: 100,
      },
      {
        title: 'Data identificării',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 130,
        render: (v: string) => new Date(v).toLocaleDateString('ro-RO'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (v: LesionStatus) => <Tag color={statusColor[v]}>{v}</Tag>,
      },
      {
        title: 'Acțiuni',
        key: 'actions',
        width: 180,
        render: (_: unknown, l: LesionFull) => (
          <Space size="small">
            <Button
              size="small"
              icon={<AimOutlined />}
              onClick={() => selectLesion(l.id ?? null)}
              type={l.id === undefined ? 'default' : 'primary'}
            >
              Selectează
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(l)} />
            <Popconfirm
              title="Șterge leziune?"
              description="Soft delete — poate fi restaurată."
              onConfirm={() => l.id && void removeLesion(l.id)}
              okText="Da"
              cancelText="Nu"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [removeLesion, selectLesion]
  );

  if (loading || !form) {
    return <Card loading title="Leziuni cutanate" />;
  }

  return (
    <Card
      title={
        <Space>
          <EyeOutlined style={{ color: '#1890ff' }} />
          <span>Leziuni cutanate — inventar pentru {form.id.slice(0, 8)}</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Adaugă leziune
        </Button>
      }
    >
      {lesions.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical">
              <Text>Nu există leziuni înregistrate încă.</Text>
              <Text type="secondary">Adaugă prima leziune pentru a continua screening-ul.</Text>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Adaugă prima leziune
            </Button>
            <Button
              data-testid="no-lesions-continue"
              onClick={() => patient && navigate(`/patients/${patient.id}/dermato-wizard/8`)}
            >
              Fără leziuni — continuă la rezultat screening
            </Button>
          </Space>
        </Empty>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message={`${lesions.length} leziuni inventariate`}
            style={{ marginBottom: 12 }}
          />
          <Table
            scroll={{ x: 'max-content' }}
            rowKey="id"
            dataSource={lesions}
            columns={columns as any}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        </>
      )}

      <Modal
        title={editing ? `Editare leziune ${editing.clinicalCode}` : 'Adaugă leziune nouă'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={saving}
        okText={editing ? 'Salvează' : 'Creează'}
        cancelText="Anulează"
        width={640}
        destroyOnClose
      >
        <Form form={formAntd} layout="vertical" preserve={false}>
          <Form.Item name="type" label="Tip leziune" rules={[{ required: true }]}>
            <Select options={LESION_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="customType" label="Tip custom (dacă OTHER)">
            <Input placeholder="ex: keratoză seboreică" />
          </Form.Item>
          <Form.Item name="location" label="Localizare anatomică" rules={[{ required: true }]}>
            <Input placeholder="ex: spate – regiune scapulară dreaptă" />
          </Form.Item>
          <Form.Item name="laterality" label="Lateralitate">
            <Select options={LATERALITY_OPTIONS} allowClear />
          </Form.Item>
          <Form.Item name="fitzpatrickType" label="Tip Fitzpatrick (piele)">
            <Select options={FITZPATRICK_OPTIONS} allowClear />
          </Form.Item>
          <Form.Item name="size" label="Dimensiune" rules={[{ required: true }]}>
            <Input placeholder="ex: 8x5 mm" />
          </Form.Item>
          <Form.Item name="color" label="Culoare">
            <Input placeholder="ex: maro neuniform" />
          </Form.Item>
          <Form.Item name="border" label="Margini">
            <Input placeholder="ex: neregulate" />
          </Form.Item>
          <Form.Item name="surface" label="Suprafață">
            <Input placeholder="ex: reliefată" />
          </Form.Item>
          <Form.Item name="onsetDate" label="Data apariției">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="recentChange" label="Modificare recentă" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isNewlyDiagnosed" label="Leziune nou diagnosticată" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>

          <Collapse
            items={[
              {
                key: 'ak',
                label: 'Keratoză actinică (AK) — fenomenotip',
                children: (
                  <div>
                    <Form.Item label="Leziune de tip keratoză actinică (AK)" style={{ marginBottom: 12 }}>
                      <Switch
                        checked={akEnabled}
                        onChange={(checked: boolean) => {
                          setAkEnabled(checked);
                          if (!checked) {
                            setAk({});
                            setAkErrors([]);
                          }
                        }}
                        data-testid="ak-enabled-switch"
                      />
                    </Form.Item>

                    {akEnabled && (
                      <>
                        <Form.Item label="Fenomenotip AK" required style={{ marginBottom: 12 }}>
                          <Select
                            value={ak.phenotype ?? undefined}
                            onChange={(value: string) =>
                              updateAk({ phenotype: value as AkPhenotypeType })
                            }
                            options={AK_PHENOTYPE_OPTIONS}
                            placeholder="Alege fenomenotipul AK"
                            data-testid="ak-phenotype-select"
                          />
                        </Form.Item>

                        <Form.Item label="Severitate (grad)" style={{ marginBottom: 12 }}>
                          <Select
                            value={ak.severity ?? undefined}
                            onChange={(value: string) => updateAk({ severity: value })}
                            options={AK_SEVERITY_OPTIONS}
                            allowClear
                            placeholder="ex: Grad I"
                            data-testid="ak-severity-select"
                          />
                        </Form.Item>

                        <Form.Item label="Zonă anatomică" style={{ marginBottom: 12 }}>
                          <Select
                            value={ak.bodyZone ?? undefined}
                            onChange={(value: string) => updateAk({ bodyZone: value })}
                            options={BODY_ZONE_OPTIONS}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="ex: Fața dorsală a mâinii"
                            data-testid="ak-body-zone-select"
                          />
                        </Form.Item>

                        {ak.bodyZone === 'HEAD' && (
                          <Form.Item label="Regiune facială" required style={{ marginBottom: 12 }}>
                            <Select
                              value={ak.faceZone ?? undefined}
                              onChange={(value: string) => updateAk({ faceZone: value })}
                              options={FACE_ZONE_OPTIONS}
                              showSearch
                              optionFilterProp="label"
                              placeholder="ex: Temporal, Obaz, Nazal"
                              data-testid="ak-face-zone-select"
                            />
                          </Form.Item>
                        )}

                        {ak.phenotype === AkPhenotypeType.SPECIAL_AREA && (
                          <Form.Item label="Zonă specială AK" required style={{ marginBottom: 12 }}>
                            <Select
                              value={ak.specialArea ?? undefined}
                              onChange={(value: string) => updateAk({ specialArea: value })}
                              options={AK_SPECIAL_AREA_OPTIONS}
                              allowClear
                              placeholder="ex: Buză inferioară"
                              data-testid="ak-special-area-select"
                            />
                          </Form.Item>
                        )}

                        {ak.phenotype === AkPhenotypeType.IMMUNOCOMPROMISED && (
                          <Form.Item label="Tip de imunosupresie" style={{ marginBottom: 12 }}>
                            <Input
                              value={ak.immunosuppressionType ?? ''}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateAk({ immunosuppressionType: e.target.value })
                              }
                              placeholder="ex: Metotrexat, transplant de organ"
                              data-testid="ak-immunosuppression-input"
                            />
                          </Form.Item>
                        )}

                        <Form.Item
                          label="Dimensiuni (mm)"
                          style={{ marginBottom: 12 }}
                          extra="Obligatoriu pentru AK extinsă (câmp pe zonă vizibilă sau AK difuză)."
                        >
                          <Input
                            value={ak.dimensionsMm ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              updateAk({ dimensionsMm: e.target.value })
                            }
                            placeholder="ex: 20 × 15 mm"
                            data-testid="ak-dimensions-input"
                          />
                        </Form.Item>

                        <Form.Item
                          label="Evoluție în dinamică"
                          style={{ marginBottom: 12 }}
                          extra="Obligatoriu pentru AK extinsă (câmp pe zonă vizibilă sau AK difuză)."
                        >
                          <Input
                            value={ak.evolutionDynamics ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              updateAk({ evolutionDynamics: e.target.value })
                            }
                            placeholder="ex: în creștere lentă, s-a extins în ultimele 6 luni"
                            data-testid="ak-dynamics-input"
                          />
                        </Form.Item>
                      </>
                    )}

                    {akErrors.length > 0 && (
                      <Alert
                        type="error"
                        showIcon
                        message="Datele AK sunt incomplete"
                        description={
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {akErrors.map((err) => (
                              <li key={err}>{err}</li>
                            ))}
                          </ul>
                        }
                        data-testid="ak-errors"
                      />
                    )}
                  </div>
                ),
              },
            ]}
          />

          {form && (
            <Form.Item name="photoIds" label="Imagini">
              <LesionPhotos formId={form.id} />
            </Form.Item>
          )}
          <Form.Item name="description" label="Descriere">
            <Input.TextArea rows={3} placeholder="Observații clinice…" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Step4Lesions;
